// Keep the homepage's initial rendering independent of the optional 3D viewer.
class EchtPBHome extends HTMLElement {
  #observer; #viewer; #generation = 0; #idle; #timer; #load;
  #launchTimer; #launchRefresh;
  connectedCallback() {
    const generation = ++this.#generation;
    this.#startLaunchClock();
    // The linked poster is the complete fallback on constrained connections.
    if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '') || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const start = () => {
      this.#load = undefined;
      if (!this.isConnected || generation !== this.#generation) return;
      this.#observer = new IntersectionObserver(([entry]) => {
        this.#cancelScheduled();
        if (!entry.isIntersecting) return;
        const mount = async () => {
          this.#observer.disconnect();
          try {
            const { mountTeaser } = await import(this.dataset.viewerUrl);
            if (!this.isConnected || generation !== this.#generation) return;
            const viewer = await mountTeaser(this.querySelector('[data-pb-home-box]'));
            if (!this.isConnected || generation !== this.#generation) viewer.dispose();
            else this.#viewer = viewer;
          } catch { /* Keep the linked poster when WebGL or a request fails. */ }
        };
        if ('requestIdleCallback' in window) this.#idle = requestIdleCallback(mount);
        else this.#timer = setTimeout(mount, 200);
      }, { rootMargin: '150px' });
      this.#observer.observe(this);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', this.#load = start, { once: true });
  }
  #startLaunchClock() {
    this.#stopLaunchClock();
    const raw = this.dataset.launchAt || '';
    const link = this.dataset.launchLink;
    const parts = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:Z|[+-]\d{2}:\d{2})$/);
    const deadline = Date.parse(raw);
    // Reject invalid and normalized-overflow dates; absent config keeps early access.
    if (!link || !parts || !Number.isFinite(deadline) || deadline <= 0 ||
        +parts[2] < 1 || +parts[2] > 12 || +parts[3] < 1 ||
        +parts[3] > new Date(Date.UTC(+parts[1], +parts[2], 0)).getUTCDate() ||
        +parts[4] > 23 || +parts[5] > 59 || +parts[6] > 59) return;
    this.#launchRefresh = () => {
      clearTimeout(this.#launchTimer);
      if (!this.isConnected) return;
      const remaining = deadline - Date.now();
      if (remaining > 0) {
        this.#launchTimer = setTimeout(this.#launchRefresh, Math.min(remaining, 60000));
        return;
      }
      for (const anchor of this.querySelectorAll('.echt-pb-home__visual, .echt-pb-home__link')) anchor.href = link;
      const button = this.querySelector('.echt-pb-home__link');
      if (button) button.textContent = (this.dataset.launchButtonLabel || 'Shop The PB Edit').toUpperCase();
      const date = this.querySelector('.echt-pb-home__date');
      if (date) date.textContent = 'Available now';
      this.#stopLaunchClock();
    };
    window.addEventListener('pageshow', this.#launchRefresh);
    document.addEventListener('visibilitychange', this.#launchRefresh);
    this.#launchRefresh();
  }
  #stopLaunchClock() {
    clearTimeout(this.#launchTimer);
    this.#launchTimer = undefined;
    if (this.#launchRefresh) {
      window.removeEventListener('pageshow', this.#launchRefresh);
      document.removeEventListener('visibilitychange', this.#launchRefresh);
      this.#launchRefresh = undefined;
    }
  }
  #cancelScheduled() {
    if (this.#idle !== undefined) cancelIdleCallback(this.#idle);
    clearTimeout(this.#timer);
    this.#idle = this.#timer = undefined;
  }
  disconnectedCallback() {
    this.#stopLaunchClock();
    this.#generation++;
    if (this.#load) window.removeEventListener('load', this.#load);
    this.#cancelScheduled();
    this.#observer?.disconnect();
    this.#viewer?.dispose();
    this.#viewer = undefined;
  }
}
if (!customElements.get('echt-pb-home')) customElements.define('echt-pb-home', EchtPBHome);
